/**
 * @fileoverview Service layer for Student Signup
 * @description API calls for student invite signup flow
 */

import { api, EmailAlreadyRegisteredError } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type {
  StudentSignupData,
  StudentSignupResponse,
  InviteCodeValidation,
  FirebaseUser,
  FirebaseTokenManager,
} from './types'

/**
 * Check if errors indicate email is already registered
 */
const isEmailAlreadyRegistered = (errors: Record<string, string[]>): boolean => {
  const emailErrors = errors?.email
  if (!Array.isArray(emailErrors)) return false
  return emailErrors.some(msg =>
    msg.toLowerCase().includes('already exists') ||
    msg.toLowerCase().includes('has already been taken')
  )
}

/**
 * Format error messages from API response
 * Handles both array and object formats
 * @throws EmailAlreadyRegisteredError when email already exists
 */
const formatErrorMessages = (errors: unknown): string => {
  if (!errors) return 'Erro ao processar solicitação'

  // Array format: ['error1', 'error2']
  if (Array.isArray(errors)) {
    return errors.join('. ')
  }

  // Object format: { email: ['already exists'], password: ['is required'] }
  if (typeof errors === 'object') {
    if (isEmailAlreadyRegistered(errors as Record<string, string[]>)) {
      throw new EmailAlreadyRegisteredError()
    }
    const messages: string[] = []
    Object.entries(errors as Record<string, string[]>).forEach(([field, fieldErrors]) => {
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(err => {
          messages.push(`${field}: ${err}`)
        })
      }
    })
    return messages.join('. ') || 'Erro ao processar solicitação'
  }

  return 'Erro ao processar solicitação'
}

/**
 * Validate an invite code using tutor invite endpoint.
 * Group invites are no longer used — all invites are tutor-level.
 */
export const validateInviteCode = async (code: string): Promise<InviteCodeValidation> => {
  try {
    const response = await api.get(
      ARC_MOSAICO_ENDPOINTS.tutorInvites.validate(code)
    )
    if (response.data?.valid) {
      return {
        valid: true,
        tutor: response.data.tutor,
        school: response.data.school,
        inviteType: 'tutor',
      }
    }
    return { valid: false, error: 'Código de convite inválido ou expirado' }
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number; data?: { error?: string } } }
    if (axiosError.response?.status === 422) {
      return {
        valid: false,
        error: axiosError.response.data?.error || 'Código de convite inválido ou expirado',
      }
    }
    return { valid: false, error: 'Erro ao validar código de convite' }
  }
}

/**
 * Create a student account with email/password
 */
export const createStudent = async (data: StudentSignupData): Promise<StudentSignupResponse> => {
  try {
    const response = await api.post<StudentSignupResponse>(
      ARC_MOSAICO_ENDPOINTS.auth.inviteSignup,
      {
        inviteToken: data.inviteCode,
        username: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      }
    )
    return response.data
  } catch (error: unknown) {
    // Handle axios errors (API returned error response)
    const axiosError = error as { response?: { data?: { errors?: unknown; message?: string; error?: string } } }
    if (axiosError.response?.data) {
      const { data: errorData } = axiosError.response
      if (errorData.errors) {
        throw new Error(formatErrorMessages(errorData.errors))
      }
      throw new Error(errorData.message || errorData.error || 'Erro ao criar conta')
    }

    // Handle other errors
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Erro de conexão. Verifique sua internet.')
  }
}

/**
 * Create a student account with Google OAuth
 */
export const googleSignupStudent = async (
  firebaseUser: FirebaseUser,
  firebaseToken: FirebaseTokenManager | undefined,
  inviteCode: string
): Promise<StudentSignupResponse> => {
  try {
    const response = await api.post<StudentSignupResponse>(
      ARC_MOSAICO_ENDPOINTS.auth.inviteSignup,
      {
        inviteToken: inviteCode,
        email: firebaseUser.email,
        firstName: firebaseUser.firstName || firebaseUser.displayName?.split(' ')[0] || '',
        lastName: firebaseUser.lastName || firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
        firebaseUser: {
          email: firebaseUser.email,
          uid: firebaseUser.uid,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          firstName: firebaseUser.firstName,
          lastName: firebaseUser.lastName,
        },
        session: firebaseToken ? {
          firebaseUser: {
            stsTokenManager: {
              expirationTime: firebaseToken.expirationTime,
              accessToken: firebaseToken.accessToken,
              refreshToken: firebaseToken.refreshToken,
            },
          },
        } : undefined,
      }
    )
    return response.data
  } catch (error: unknown) {
    // Handle axios errors (API returned error response)
    const axiosError = error as { response?: { data?: { errors?: unknown; message?: string; error?: string } } }

    if (axiosError.response?.data) {
      const { data: errorData } = axiosError.response
      if (errorData.errors) {
        throw new Error(formatErrorMessages(errorData.errors))
      }
      throw new Error(errorData.message || errorData.error || 'Erro ao criar conta com Google')
    }

    // Handle other errors
    if (error instanceof Error) {
      throw error
    }
    throw new Error('Erro de conexão. Verifique sua internet.')
  }
}
